"use client";
import React from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { LocationOn, LocalAirport, LocalHospital } from "@mui/icons-material";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import parse from "autosuggest-highlight/parse";
import { debounce } from "@mui/material/utils";
import Skeleton from "@mui/material/Skeleton";

const GOOGLE_MAPS_API_KEY = process.env.NEXT_PUBLIC_API_KEY;

function loadScript(src, position, id) {
  if (!position) {
    return;
  }
  // Check if the script is already present on the page
  const existingScript = document.getElementById(id);
  if (existingScript) {
    return; // If the script is already present, do nothing
  }
  // Create a new script element
  const script = document.createElement("script");
  script.setAttribute("async", "");
  script.setAttribute("defer", ""); // Ensure defer loading
  script.setAttribute("id", id);
  script.src = src;
  // Handle errors during script loading
  script.onerror = () => {
    console.error(`Failed to load script: ${src}`);
  };
  // Append the script to the specified position (usually the <head> tag)
  position.appendChild(script);
}

const LocationInputs = ({
  id,
  label,
  text,
  value,
  inputValue,
  onChange,
  onInputChange,
  placeholder,
  error,
  setShowMap = () => {},
  setIsLoaded = () => {},
  isLoaded = true,
}) => {
  const loaded = React.useRef(false);
  const autocompleteService = React.useRef(null);
  const [options, setOptions] = React.useState([]);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      // Check if Google Maps is already loaded
      if (window.google && window.google.maps && window.google.maps.places) {
        // If loaded, directly initialize the AutocompleteService
        autocompleteService.current =
          new window.google.maps.places.AutocompleteService();
        loaded.current = true;
        setShowMap(true);
        setIsLoaded(true); // Enable input once API is loaded
      } else {
        // Declare the callback function in the global scope
        window.onGoogleMapsLoad = () => {
          if (
            window.google &&
            window.google.maps &&
            window.google.maps.places
          ) {
            autocompleteService.current =
              new window.google.maps.places.AutocompleteService();
            loaded.current = true;
            setShowMap(true);
            setIsLoaded(true); // Enable input once API is loaded
          } else {
            console.error("Google Maps API failed to load properly.");
          }
        };
        if (!loaded.current && !document.querySelector("#google-maps")) {
          // Reset loaded.current to false when the component unmounts and mounts again
          loaded.current = false;
          // Load the script only if it hasn't been loaded yet
          loadScript(
            `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&callback=onGoogleMapsLoad`,
            document.querySelector("head"),
            "google-maps"
          );
        }
      }
    }
    return () => {
      loaded.current = false;
      window.onGoogleMapsLoad = null; // Clean up the callback when unmounted
    };
  }, [setShowMap, setIsLoaded]);

  const fetch = React.useMemo(
    () =>
      debounce((request, callback) => {
        let requestOptions = { ...request };
        if (placeholder === "Enter your complete address") {
          // No country restrictions
          requestOptions = { ...request, componentRestrictions: null };
        } else {
          requestOptions = {
            ...request,
            componentRestrictions: { country: "US" },
          };
        }
        // Fetch predictions from Google Places Autocomplete
        autocompleteService.current.getPlacePredictions(
          requestOptions,
          (predictions) => {
            if (predictions) {
              // Prioritize airports first in the predictions
              const sortedPredictions = predictions.sort((a, b) => {
                // Check if the prediction types contain "airport"
                const isAirportA = a.types.includes("airport");
                const isAirportB = b.types.includes("airport");
                // Airports first, then other types
                if (isAirportA && !isAirportB) {
                  return -1; // a comes before b
                } else if (!isAirportA && isAirportB) {
                  return 1; // b comes before a
                } else {
                  return 0; // Maintain original order if both or neither are airports
                }
              });
              // Call the original callback with sorted predictions
              callback(sortedPredictions);
            }
          }
        );
      }, 400), // 400ms debounce delay
    [] // Recompute when placeholder changes
  );

  React.useEffect(() => {
    let active = true;

    if (!autocompleteService.current && window.google) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch({ input: inputValue }, (results) => {
      if (active) {
        let newOptions = [];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  return (
    <>
      {!isLoaded ? (
        // Show skeleton loader while the API is loading
        <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
      ) : (
        <Autocomplete
          id={id}
          //   sx={{ width: 400, }}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option.description
          }
          isOptionEqualToValue={(option, val) =>
            option.description === val.description
          }
          filterOptions={(x) => x}
          options={options}
          autoComplete
          includeInputInList
          filterSelectedOptions
          value={value}
          noOptionsText="No locations"
          onChange={onChange}
          onInputChange={onInputChange}
          renderInput={(params) => (
            <div style={{ position: "relative" }}>
              <LocationOn
                color="action"
                style={{
                  position: "absolute",
                  top: error ? "35%" : "50%",
                  left: 10,
                  transform: "translateY(-50%)",
                }}
              />
              <TextField
                {...params}
                label={label}
                className="customAutocomplete"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor: error ? "" : "#2B3252",
                    },
                }}
                // color="secondary"
                placeholder={placeholder}
                error={error && !value}
                helperText={error && !value ? `${text} is required.` : ""}
              />
            </div>
          )}
          renderOption={(props, option) => {
            const matches =
              option.structured_formatting.main_text_matched_substrings || [];

            const parts = parse(
              option.structured_formatting.main_text,
              matches.map((match) => [
                match.offset,
                match.offset + match.length,
              ])
            );

            // Extract the "key" prop
            const { key, ...otherProps } = props;

            return (
              <li key={key} {...otherProps}>
                <Grid container alignItems="center">
                  <Grid item sx={{ display: "flex", width: 44 }}>
                    {option && option?.types.includes("airport") ? (
                      <LocalAirport sx={{ color: "text.secondary" }} />
                    ) : option && option?.types.includes("hospital") ? (
                      <LocalHospital sx={{ color: "text.secondary" }} />
                    ) : (
                      <LocationOn sx={{ color: "text.secondary" }} />
                    )}
                  </Grid>
                  <Grid
                    item
                    sx={{ width: "calc(100% - 44px)", wordWrap: "break-word" }}
                  >
                    {parts.map((part, index) => (
                      <Box
                        key={index}
                        component="span"
                        sx={{ fontWeight: part.highlight ? "bold" : "regular" }}
                        style={{ fontSize: "14px" }}
                      >
                        {part.text}
                      </Box>
                    ))}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      style={{ fontSize: "14px" }}
                    >
                      {option.structured_formatting.secondary_text}
                    </Typography>
                  </Grid>
                </Grid>
              </li>
            );
          }}
        />
      )}
    </>
  );
};

export default LocationInputs;
